import lodashGet from 'lodash/get';
import startCase from 'lodash/startCase';
import { Properties as AnalyticProperties } from '@adsk/acs-web-analytics';
import { ToolIdType } from '@adsk/alloy-provider';
import { emptyField, screenType, workflowTypesMapper } from '../constants/analytics';
import { RfiType } from '../sdk/types';
import { removeEmptyKeys } from './dataHelpers';

export const getFileNameExtension = (fileName: any) => {
  if (!fileName) {
    return emptyField;
  }

  const fileNameArray = fileName.split('.');
  if (fileNameArray.length === 1) {
    // Return empty string if we can't extract the extension from the filename
    return '';
  }

  return fileNameArray.pop().toLowerCase();
};

export const createIssue = ({
  id,
  document,
  status,
  origin,
  uploadingAttachments,
  workflowType,
  isCreateAnother,
  rfiCustomIdentifierData,
  costImpact,
  scheduleImpact,
  priority,
  category,
  discipline,
  reference,
  locations,
  isDefaultRfiType,
  rfiTypesFieldsInUse = [],
  numCustomAttributeTypeDefaults = 0,
  hasPushpin = false,
}: any = {}) => {
  const extension = getFileNameExtension(lodashGet(document, 'fileName'));
  const statusInStartCase = status ? startCase(status) : emptyField;
  const attachmentsCount = uploadingAttachments ? uploadingAttachments.length : emptyField;

  const properties: any = {
    rfi_id: id,
    origin: origin || screenType.OTHER,
    workflow_type:
      workflowTypesMapper[workflowType as keyof typeof workflowTypesMapper] ||
      workflowTypesMapper.NONE,
    status: statusInStartCase.toUpperCase(),
    extension,
    attachments: attachmentsCount,
    create_another: !!isCreateAnother,
    cost_impact: costImpact && costImpact.toLowerCase(),
    schedule_impact: scheduleImpact && scheduleImpact.toLowerCase(),
    priority: priority && priority.toLowerCase(),
    category: category && category.map((i: any) => i.toLowerCase()),
    discipline: discipline && discipline.map((i: any) => i.toLowerCase()),
    reference,
    number_locations: locations?.length ?? 0,
    types_default_fields_number: rfiTypesFieldsInUse.length,
    types_default_fields_list: rfiTypesFieldsInUse,
    number_custom_fields_with_type_defaults: numCustomAttributeTypeDefaults,
    has_pushpin: Boolean(hasPushpin),
  };

  properties.rfi_type = isDefaultRfiType ? 'Default' : 'Other';
  if (status === 'draft') {
    delete properties.workflow_type;
  }

  if (rfiCustomIdentifierData) {
    const { isEdited, nextAvailableNumberClickedCount, validationErrorShownCount } =
      rfiCustomIdentifierData;

    properties.number_field_edited = isEdited;
    properties.next_available_number_clicked = nextAvailableNumberClickedCount;
    properties.number_duplication_error_shown = validationErrorShownCount;
  }

  return {
    eventName: 'rfi.create',
    properties,
  };
};

export const getRfisListTrackEvent = (
  origin: ToolIdType | undefined | 'rfis_module',
  role: string,
) => ({
  eventName: 'rfi.list.view',
  properties: {
    origin,
    role,
  },
});

const getRfiTypeAnalyticsProperties = (rfiType: RfiType) => {
  const {
    id,
    wfType,
    projectReviewer,
    projectCoordinator,
    manager,
    watchers,
    isDefault,
    dueDateOffset,
    locationDescription,
    costImpact,
    scheduleImpact,
    priority,
    discipline,
    category,
    reference,
    bridgeTargetProjectIds,
    customAttributes,
  } = rfiType;
  return removeEmptyKeys({
    rfi_type_id: id,
    workflow_type: wfType === 'US' ? 'a' : 'b',
    num_reviewers: projectReviewer?.length || 0,
    num_coordinator: projectCoordinator?.length || 0,
    num_manager: manager?.length || 0,
    num_watchers: watchers?.length || 0,
    is_default: !!isDefault,
    due_date_offset: dueDateOffset,
    location_details: locationDescription,
    cost_impact: costImpact,
    schedule_impact: scheduleImpact,
    priority,
    discipline,
    category,
    external_id: reference,
    num_share_with_projects: bridgeTargetProjectIds?.length || 0,
    num_custom_field_defaults: customAttributes?.length || 0,
  });
};

export const addRfiTypeTrackEvent = (rfiType: RfiType): [string, AnalyticProperties] => {
  const eventName = 'rfi.settings.rfi_types.add';
  const properties = getRfiTypeAnalyticsProperties(rfiType);

  return [eventName, properties];
};

export const editRfiTypeTrackEvent = (rfiType: RfiType): [string, AnalyticProperties] => {
  const eventName = 'rfi.settings.rfi_types.edit';
  const properties = getRfiTypeAnalyticsProperties(rfiType);

  return [eventName, properties];
};

export const deleteRfiTypeTrackEvent = (rfiType: RfiType): [string, AnalyticProperties] => {
  const eventName = 'rfi.settings.rfi_types.delete';
  const properties = {
    rfi_type_id: rfiType.id,
  };

  return [eventName, properties];
};
